(function () {
  'use strict';

  var HIGHLIGHT_DURATION = 2400;
  var RESULTS_PAGE_SIZE = 6;

  function normalizeText(value) {
    return String(value || '')
      .replace(/[™®]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  function compact(value) {
    return normalizeText(value).replace(/\s+/g, '');
  }

  function tokensMatchFields(query, values) {
    var queryTokens = normalizeText(query).split(' ').filter(Boolean);
    var fieldTokens = values.map(normalizeText).join(' ').split(' ').filter(Boolean);

    if (queryTokens.length < 2) return false;

    return queryTokens.every(function (queryToken) {
      return fieldTokens.some(function (fieldToken) {
        return fieldToken.indexOf(queryToken) === 0 || queryToken.indexOf(fieldToken) === 0;
      });
    });
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function unique(values) {
    return values.filter(function (value, index, array) {
      return value && array.indexOf(value) === index;
    });
  }

  function slugify(value) {
    return normalizeText(value).replace(/\s+/g, '-');
  }

  function textValue(value) {
    if (Array.isArray(value)) {
      return value.map(function (item) { return String(item || '').trim(); }).filter(Boolean).join(', ');
    }
    return value == null ? '' : String(value).trim();
  }

  function listValue(value) {
    if (Array.isArray(value)) {
      return unique(value.map(function (item) { return textValue(item); }).filter(Boolean));
    }

    var input = textValue(value);
    if (!input) return [];

    var values = [];
    var current = '';
    var depth = 0;

    for (var index = 0; index < input.length; index += 1) {
      var character = input.charAt(index);
      if (character === '(' || character === '[' || character === '{') depth += 1;
      if (character === ')' || character === ']' || character === '}') depth = Math.max(0, depth - 1);

      if ((character === ',' || character === '\n') && depth === 0) {
        if (current.trim()) values.push(current.trim());
        current = '';
      } else {
        current += character;
      }
    }

    if (current.trim()) values.push(current.trim());
    return unique(values);
  }

  function normalizeDevice(device, index) {
    var programName = textValue(device.programName);
    var categoryName = textValue(device.categoryName);
    var normalized = Object.assign({}, device);

    normalized.id = textValue(device.id) || 'certified-device-' + String(index + 1);
    normalized.program = textValue(device.program) || slugify(programName);
    normalized.programName = programName;
    normalized.category = textValue(device.category) || slugify(categoryName);
    normalized.categoryName = categoryName;
    normalized.manufacturer = textValue(device.manufacturer);
    normalized.productName = textValue(device.productName);
    normalized.modelNumber = textValue(device.modelNumber);
    normalized.series = textValue(device.series);
    normalized.specificationsNotes = textValue(device.specificationsNotes);
    normalized.compatibleModules = listValue(device.compatibleModules);
    normalized.compatiblePackages = listValue(device.compatiblePackages);
    normalized.aliases = listValue(device.aliases);
    normalized.searchable = device.searchable !== false;
    normalized.sortOrder = Number(device.sortOrder) || index + 1;
    return normalized;
  }

  function normalizeDevices(devices) {
    var seenIds = {};
    return (devices || []).map(normalizeDevice).filter(function (device) {
      if (seenIds[device.id]) return false;
      seenIds[device.id] = true;
      return true;
    });
  }

  function titleFromKey(value) {
    return textValue(value).split('-').map(function (word) {
      return word ? word.charAt(0).toUpperCase() + word.slice(1) : '';
    }).join(' ');
  }

  function ensureConfigurations(programs, categories, devices) {
    var programKeys = {};
    var categoryKeys = {};
    var nextProgramOrder = programs.reduce(function (maximum, program) {
      return Math.max(maximum, Number(program.displayOrder) || 0);
    }, 0);
    var nextCategoryOrder = categories.reduce(function (maximum, category) {
      return Math.max(maximum, Number(category.displayOrder) || 0);
    }, 0);

    programs.forEach(function (program) { programKeys[program.key] = true; });
    categories.forEach(function (category) { categoryKeys[category.key] = true; });

    devices.forEach(function (device) {
      if (device.program && !programKeys[device.program]) {
        nextProgramOrder += 10;
        programKeys[device.program] = true;
        programs.push({
          key: device.program,
          name: device.programName || titleFromKey(device.program),
          displayOrder: nextProgramOrder,
          initialExpanded: false,
          categoryOrder: [],
          columns: [
            { key: 'manufacturer', label: 'Manufacturer', fields: ['manufacturer'] },
            { key: 'productName', label: 'Product name', fields: ['productName'], conditional: true },
            { key: 'modelNumber', label: 'Model no.', fields: ['modelNumber'], conditional: true },
            { key: 'seriesValue', label: 'Series', fields: ['series'], conditional: true },
            { key: 'specificationsNotes', label: 'Specifications / Notes', fields: ['specificationsNotes'], conditional: true },
            { key: 'compatibleModules', label: 'Compatible modules', fields: ['compatibleModules'], conditional: true },
            { key: 'compatiblePackages', label: 'Compatible packages', fields: ['compatiblePackages'], conditional: true }
          ]
        });
      }

      if (device.category && !categoryKeys[device.category]) {
        nextCategoryOrder += 10;
        categoryKeys[device.category] = true;
        categories.push({
          key: device.category,
          name: device.categoryName || titleFromKey(device.category),
          displayOrder: nextCategoryOrder
        });
      }
    });
  }

  var naturalCollator = typeof Intl !== 'undefined' && Intl.Collator
    ? new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })
    : null;

  function naturalCompare(first, second) {
    var a = String(first || '');
    var b = String(second || '');
    return naturalCollator ? naturalCollator.compare(a, b) : a.localeCompare(b);
  }

  function flattenedValues(records, field) {
    return unique([].concat.apply([], records.map(function (record) {
      var value = record[field];
      if (Array.isArray(value)) return value;
      return value ? [value] : [];
    })));
  }

  function recordIdentity(record) {
    return record.modelNumber || record.series || record.productName || '';
  }

  function recordSortValue(record) {
    return record.modelNumber || record.series || record.productName || '';
  }

  function hasFieldValue(record, field) {
    var value = record[field];
    return Array.isArray(value) ? value.length > 0 : Boolean(value);
  }

  function groupDevices(devices) {
    var groups = {};

    devices.forEach(function (device) {
      var key = normalizeText(device.manufacturer) + '|' + compact(recordIdentity(device));
      if (!groups[key]) {
        groups[key] = {
          key: key,
          manufacturer: device.manufacturer,
          aliases: [],
          records: [],
          categories: [],
          productNames: [],
          modelNumbers: [],
          seriesValues: [],
          specificationsNotes: [],
          compatibleModules: [],
          compatiblePackages: []
        };
      }
      groups[key].records.push(device);
      groups[key].aliases = unique(groups[key].aliases.concat(device.aliases || []));
      groups[key].categories = unique(groups[key].categories.concat(device.category || []));
      groups[key].productNames = unique(groups[key].productNames.concat(device.productName || []));
      groups[key].modelNumbers = unique(groups[key].modelNumbers.concat(device.modelNumber || []));
      groups[key].seriesValues = unique(groups[key].seriesValues.concat(device.series || []));
      groups[key].specificationsNotes = unique(groups[key].specificationsNotes.concat(device.specificationsNotes || []));
      groups[key].compatibleModules = unique(groups[key].compatibleModules.concat(device.compatibleModules || []));
      groups[key].compatiblePackages = unique(groups[key].compatiblePackages.concat(device.compatiblePackages || []));
    });

    return Object.keys(groups).map(function (key) {
      var group = groups[key];
      group.displayLabel = group.productNames[0] || group.modelNumbers[0] || group.seriesValues[0] || '—';
      group.secondaryLabel = group.productNames.length && group.modelNumbers.length
        ? 'Model no. ' + group.modelNumbers.join(', ')
        : '';
      return group;
    });
  }

  function groupDirectoryDevices(devices) {
    var groups = {};
    var order = [];

    devices.forEach(function (device) {
      var key = normalizeText(device.manufacturer);
      if (!groups[key]) {
        groups[key] = {
          manufacturer: device.manufacturer,
          records: []
        };
        order.push(key);
      }
      groups[key].records.push(device);
    });

    return order.map(function (key) { return groups[key]; });
  }

  function directoryIdentity(record, field) {
    var base;
    var details = [];

    if (field === 'series') {
      base = record.series || '';
    } else {
      base = record.productName || record.modelNumber || '';
      if (record.productName && record.modelNumber) details.push('Model no. ' + record.modelNumber);
    }

    if (!base) return '—';
    if (record.specificationsNotes) details.push(record.specificationsNotes);
    return base + (details.length ? ' (' + details.join('; ') + ')' : '');
  }

  function contextMatchesRecord(record, query) {
    var queryText = normalizeText(query);
    var manufacturerText = normalizeText(record.manufacturer);
    var categoryText = normalizeText(record.category);

    if (!queryText) return false;
    return manufacturerText.indexOf(queryText) !== -1 ||
      categoryText.indexOf(queryText) !== -1 ||
      tokensMatchFields(queryText, [manufacturerText, categoryText]);
  }

  function groupContextResults(devices, query) {
    var groups = {};

    devices.forEach(function (record) {
      if (!contextMatchesRecord(record, query)) return;
      var key = normalizeText(record.manufacturer) + '|' + normalizeText(record.category);
      if (!groups[key]) {
        groups[key] = {
          key: key,
          isContextResult: true,
          manufacturer: record.manufacturer,
          category: record.category,
          categories: [record.category],
          records: [],
          compatibleModules: [],
          compatiblePackages: []
        };
      }
      groups[key].records.push(record);
      groups[key].compatibleModules = unique(groups[key].compatibleModules.concat(record.compatibleModules || []));
      groups[key].compatiblePackages = unique(groups[key].compatiblePackages.concat(record.compatiblePackages || []));
    });

    return Object.keys(groups).map(function (key) {
      return groups[key];
    }).sort(function (a, b) {
      return naturalCompare(a.manufacturer, b.manufacturer) ||
        naturalCompare(a.category, b.category);
    });
  }

  function primarySearchValues(group) {
    return unique(
      group.modelNumbers
        .concat(group.productNames)
        .concat(group.seriesValues)
        .concat(group.aliases)
    );
  }

  function textContains(value, queryText, queryCompact) {
    var normalized = normalizeText(value);
    return normalized.indexOf(queryText) !== -1 || compact(value).indexOf(queryCompact) !== -1;
  }

  function exactMatch(group, query) {
    var queryText = normalizeText(query);
    var queryCompact = compact(query);
    var manufacturer = normalizeText(group.manufacturer);
    var candidates = [];

    primarySearchValues(group).forEach(function (value) {
      var normalizedValue = normalizeText(value);
      candidates.push(normalizedValue, manufacturer + ' ' + normalizedValue);
    });

    return candidates.some(function (candidate) {
      return queryText === candidate || queryCompact === compact(candidate);
    });
  }

  function relatedScore(group, query) {
    var queryText = normalizeText(query);
    var queryCompact = compact(query);
    var manufacturerText = normalizeText(group.manufacturer);
    var categoryText = group.categories.map(normalizeText).join(' ');
    var primaryValues = primarySearchValues(group);
    var nonAliasPrimary = unique(group.modelNumbers.concat(group.productNames).concat(group.seriesValues));
    var lowerPriorityValues = group.specificationsNotes
      .concat(group.compatibleModules)
      .concat(group.compatiblePackages);

    if (nonAliasPrimary.some(function (value) {
      return normalizeText(value).indexOf(queryText) === 0 || compact(value).indexOf(queryCompact) === 0;
    })) return 1;
    if (group.aliases.some(function (alias) {
      return normalizeText(alias).indexOf(queryText) === 0 || compact(alias).indexOf(queryCompact) === 0;
    })) return 2;
    if (primaryValues.some(function (value) { return textContains(value, queryText, queryCompact); })) return 3;
    if (primaryValues.some(function (value) {
      return textContains(group.manufacturer + ' ' + value, queryText, queryCompact);
    }) && manufacturerText.indexOf(queryText) === -1) return 4;
    if (tokensMatchFields(queryText, [manufacturerText, categoryText])) return 5;
    if (manufacturerText.indexOf(queryText) !== -1) return 6;
    if (categoryText.indexOf(queryText) !== -1) return 7;
    if (lowerPriorityValues.some(function (value) { return textContains(value, queryText, queryCompact); })) return 8;
    return Infinity;
  }

  function searchGroups(groups, query) {
    var exact = [];
    var related = [];

    if (!normalizeText(query)) return { exact: exact, related: related };

    groups.forEach(function (group) {
      if (exactMatch(group, query)) {
        exact.push(group);
        return;
      }
      var score = relatedScore(group, query);
      if (Number.isFinite(score)) related.push({ group: group, score: score });
    });

    function alphaSort(a, b) {
      return naturalCompare(a.manufacturer, b.manufacturer) || naturalCompare(a.displayLabel, b.displayLabel);
    }

    exact.sort(alphaSort);
    related.sort(function (a, b) {
      return a.score - b.score || alphaSort(a.group, b.group);
    });

    return {
      exact: exact,
      related: related.map(function (item) { return item.group; })
    };
  }

  function resultPage(results, startIndex) {
    return results.slice(startIndex, startIndex + RESULTS_PAGE_SIZE);
  }

  function PureAssurance(root) {
    this.root = root;
    this.sectionId = root.getAttribute('data-section-id') || '';
    this.iconScope = root.getAttribute('data-icon-scope') || '';
    this.data = JSON.parse(root.querySelector('[data-pl-data]').textContent);
    this.programs = (this.data.programs || []).slice();
    this.categories = (this.data.categories || []).slice();
    this.devices = this.data.devices || [];
    this.form = root.querySelector('[data-pl-search-form]');
    this.searchShell = root.querySelector('.pl-pureassurance__search-shell');
    this.input = root.querySelector('[data-pl-search-input]');
    this.results = root.querySelector('[data-pl-results]');
    this.summary = root.querySelector('[data-pl-result-summary]');
    this.state = root.querySelector('[data-pl-result-state]');
    this.clearButton = root.querySelector('[data-pl-clear]');
    this.exactSection = root.querySelector('[data-pl-exact-section]');
    this.relatedSection = root.querySelector('[data-pl-related-section]');
    this.exactHeading = root.querySelector('[data-pl-exact-heading]');
    this.relatedHeading = root.querySelector('[data-pl-related-heading]');
    this.exactResults = root.querySelector('[data-pl-exact-results]');
    this.relatedResults = root.querySelector('[data-pl-related-results]');
    this.moreResultsButton = root.querySelector('[data-pl-more-results]');
    this.directory = root.querySelector('[data-pl-directory]');
    this.relatedMatches = [];
    this.visibleRelatedCount = 0;
    this.exactMatchCount = 0;
    this.currentQuery = '';
    this.handleOutsidePointerDown = this.onOutsidePointerDown.bind(this);
    this.handleTooltipResize = this.positionActiveTooltip.bind(this);
    this.activeTooltipTarget = null;
    this.tooltip = null;

    this.refreshDataModel();
    this.renderDirectory();
    this.setupTooltips();
    this.bindEvents();
    this.loadAdditionalMetaobjectPages();
  }

  PureAssurance.prototype.refreshDataModel = function () {
    this.devices = normalizeDevices(this.devices);
    ensureConfigurations(this.programs, this.categories, this.devices);
    this.programs.sort(function (a, b) { return a.displayOrder - b.displayOrder; });
    this.categories.sort(function (a, b) { return a.displayOrder - b.displayOrder; });
    this.groups = groupDevices(this.devices.filter(function (device) {
      return device.searchable !== false;
    }));
    this.programMap = {};
    this.categoryMap = {};
    this.programs.forEach(function (program) { this.programMap[program.key] = program; }, this);
    this.categories.forEach(function (category) { this.categoryMap[category.key] = category; }, this);
  };

  PureAssurance.prototype.fetchMetaobjectPage = function (pageNumber, pageParam) {
    var url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.delete('sections');
    url.searchParams.set('section_id', this.sectionId);
    url.searchParams.set(pageParam, String(pageNumber));

    return window.fetch(url.toString(), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    }).then(function (response) {
      if (!response.ok) throw new Error('Metaobject page request failed with status ' + response.status + '.');
      return response.text();
    }).then(function (html) {
      var documentFragment = new DOMParser().parseFromString(html, 'text/html');
      var dataScript = documentFragment.querySelector('[data-pl-data]');
      if (!dataScript) throw new Error('Metaobject page response did not include PureAssurance data.');
      return JSON.parse(dataScript.textContent);
    });
  };

  PureAssurance.prototype.loadAdditionalMetaobjectPages = function () {
    var self = this;
    var pagination = this.data.pagination || {};
    var totalPages = Number(pagination.pages) || 1;
    var currentPage = Number(pagination.currentPage) || 1;
    var pageParam = textValue(pagination.pageParam) || 'page';
    var pages = [];

    if (this.data.source !== 'metaobjects' || totalPages <= 1 || !this.sectionId || !window.fetch) {
      return Promise.resolve(this.devices);
    }

    for (var page = 1; page <= totalPages; page += 1) {
      if (page !== currentPage) pages.push(page);
    }

    this.root.setAttribute('aria-busy', 'true');
    this.loadingPromise = Promise.all(pages.map(function (pageNumber) {
      return self.fetchMetaobjectPage(pageNumber, pageParam);
    })).then(function (payloads) {
      payloads.forEach(function (payload) {
        self.devices = self.devices.concat(payload.devices || []);
      });
      self.refreshDataModel();
      self.renderDirectory();
      if (self.currentQuery) self.runSearch(self.currentQuery);
      self.root.removeAttribute('aria-busy');
      self.root.setAttribute('data-pl-loaded-records', String(self.devices.length));
      return self.devices;
    }).catch(function (error) {
      self.root.removeAttribute('aria-busy');
      self.root.setAttribute('data-pl-metaobject-load-error', 'true');
      if (window.console && window.console.error) window.console.error('[PureAssurance]', error);
      return self.devices;
    });

    return this.loadingPromise;
  };

  PureAssurance.prototype.setupTooltips = function () {
    this.tooltip = document.createElement('div');
    this.tooltip.className = 'pl-pureassurance-tooltip';
    this.tooltip.id = this.root.id + '-tooltip';
    this.tooltip.setAttribute('role', 'tooltip');
    this.tooltip.hidden = true;
    document.body.appendChild(this.tooltip);
    this.root.classList.add('is-tooltip-enhanced');
  };

  PureAssurance.prototype.bindEvents = function () {
    var self = this;

    this.form.addEventListener('submit', function (event) {
      event.preventDefault();
      self.runSearch(self.input.value);
    });

    this.input.addEventListener('input', function () {
      var hasValue = Boolean(self.input.value);
      self.clearButton.hidden = !hasValue;

      if (!hasValue) {
        self.results.hidden = true;
        self.clearResults();
        return;
      }

      if (normalizeText(self.input.value) !== normalizeText(self.currentQuery)) {
        self.results.hidden = true;
      }
    });

    this.input.addEventListener('search', function () {
      if (!self.input.value) {
        self.results.hidden = true;
        self.clearButton.hidden = true;
        self.clearResults();
      }
    });

    this.moreResultsButton.addEventListener('click', function () {
      self.showMoreRelatedResults();
    });

    this.form.addEventListener('reset', function () {
      window.setTimeout(function () {
        self.input.value = '';
        self.results.hidden = true;
        self.clearButton.hidden = true;
        self.clearResults();
        self.input.focus();
      }, 0);
    });

    this.root.addEventListener('click', function (event) {
      var accordionButton = event.target.closest('[data-pl-accordion-button]');
      var recordTarget = event.target.closest('[data-pl-record-id]');
      var serviceTab = event.target.closest('[data-pl-service-tab]');
      var tierToggle = event.target.closest('[data-pl-service-tier-toggle]');
      var tierOption = event.target.closest('[data-pl-service-tier-option]');
      var infoIcon = event.target.closest('.pl-pureassurance__info[data-tooltip]');

      if (accordionButton) self.toggleAccordion(accordionButton);
      if (recordTarget) self.revealRecord(recordTarget.getAttribute('data-pl-record-id'), recordTarget.getAttribute('data-pl-program'));
      if (serviceTab) self.activateServiceTab(serviceTab, false);
      if (tierToggle) self.toggleServiceTierPicker(tierToggle);
      if (tierOption) self.selectServiceTier(tierOption);
      if (infoIcon) self.showTooltip(infoIcon);
    });

    this.root.addEventListener('keydown', function (event) {
      var resultCard = event.target.closest('.pl-pureassurance__result-card[data-pl-record-id]');
      if (!resultCard || (event.key !== 'Enter' && event.key !== ' ')) return;
      event.preventDefault();
      self.revealRecord(resultCard.getAttribute('data-pl-record-id'), resultCard.getAttribute('data-pl-program'));
    });

    this.root.addEventListener('pointerover', function (event) {
      var infoIcon = event.target.closest('.pl-pureassurance__info[data-tooltip]');
      if (infoIcon) self.showTooltip(infoIcon);
    });

    this.root.addEventListener('pointerout', function (event) {
      var infoIcon = event.target.closest('.pl-pureassurance__info[data-tooltip]');
      if (!infoIcon || infoIcon.contains(event.relatedTarget)) return;
      if (!infoIcon.matches(':focus-visible')) self.hideTooltip(infoIcon);
    });

    this.root.addEventListener('focusin', function (event) {
      var infoIcon = event.target.closest('.pl-pureassurance__info[data-tooltip]');
      if (infoIcon) self.showTooltip(infoIcon);
    });

    this.root.addEventListener('focusout', function (event) {
      var infoIcon = event.target.closest('.pl-pureassurance__info[data-tooltip]');
      if (infoIcon) self.hideTooltip(infoIcon);
    });

    this.root.addEventListener('keydown', function (event) {
      var serviceTab = event.target.closest('[data-pl-service-tab]');
      var tabs;
      var currentIndex;
      var nextIndex;

      if (!serviceTab || ['ArrowLeft', 'ArrowRight', 'Home', 'End'].indexOf(event.key) === -1) return;

      tabs = Array.prototype.slice.call(self.root.querySelectorAll('[data-pl-service-tab]'));
      currentIndex = tabs.indexOf(serviceTab);
      nextIndex = currentIndex;

      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = tabs.length - 1;

      event.preventDefault();
      self.activateServiceTab(tabs[nextIndex], true);
    });

    this.root.addEventListener('keydown', function (event) {
      var tierToggle = event.target.closest('[data-pl-service-tier-toggle]');
      var tierOption = event.target.closest('[data-pl-service-tier-option]');
      var picker;
      var options;
      var currentIndex;
      var nextIndex;

      if (tierToggle) {
        if (event.key === 'Escape') {
          event.preventDefault();
          self.closeServiceTierPickers();
          return;
        }

        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

        event.preventDefault();
        self.openServiceTierPicker(tierToggle);
        picker = tierToggle.closest('[data-pl-service-tier-picker]');
        options = Array.prototype.slice.call(picker.querySelectorAll('[data-pl-service-tier-option]'));
        currentIndex = options.findIndex(function (option) {
          return option.getAttribute('aria-selected') === 'true';
        });
        nextIndex = event.key === 'ArrowUp'
          ? (currentIndex > 0 ? currentIndex - 1 : options.length - 1)
          : (currentIndex + 1) % options.length;
        options[nextIndex].focus();
        return;
      }

      if (!tierOption) return;

      picker = tierOption.closest('[data-pl-service-tier-picker]');
      options = Array.prototype.slice.call(picker.querySelectorAll('[data-pl-service-tier-option]'));
      currentIndex = options.indexOf(tierOption);
      nextIndex = currentIndex;

      if (event.key === 'Escape') {
        event.preventDefault();
        self.closeServiceTierPickers();
        picker.querySelector('[data-pl-service-tier-toggle]').focus();
        return;
      }

      if (event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % options.length;
      if (event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + options.length) % options.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = options.length - 1;
      if (nextIndex === currentIndex) return;

      event.preventDefault();
      options[nextIndex].focus();
    });

    document.addEventListener('pointerdown', this.handleOutsidePointerDown);
    window.addEventListener('resize', this.handleTooltipResize);
  };

  PureAssurance.prototype.openServiceTierPicker = function (toggle) {
    var picker;
    var menu;

    if (!toggle) return;
    picker = toggle.closest('[data-pl-service-tier-picker]');
    if (!picker) return;
    menu = picker.querySelector('[data-pl-service-tier-menu]');
    if (!menu) return;

    this.closeServiceTierPickers();
    toggle.setAttribute('aria-expanded', 'true');
    menu.hidden = false;
  };

  PureAssurance.prototype.toggleServiceTierPicker = function (toggle) {
    if (toggle.getAttribute('aria-expanded') === 'true') {
      this.closeServiceTierPickers();
      return;
    }

    this.openServiceTierPicker(toggle);
  };

  PureAssurance.prototype.closeServiceTierPickers = function () {
    this.root.querySelectorAll('[data-pl-service-tier-picker]').forEach(function (picker) {
      var toggle = picker.querySelector('[data-pl-service-tier-toggle]');
      var menu = picker.querySelector('[data-pl-service-tier-menu]');

      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      if (menu) menu.hidden = true;
    });
  };

  PureAssurance.prototype.selectServiceTier = function (option) {
    var picker = option.closest('[data-pl-service-tier-picker]');
    var panel = option.closest('[data-pl-service-panel]');
    var toggle;
    var label;
    var value;

    if (!picker || !panel) return;

    toggle = picker.querySelector('[data-pl-service-tier-toggle]');
    label = picker.querySelector('[data-pl-service-tier-label]');
    value = option.getAttribute('data-tier-value');

    panel.setAttribute('data-active-tier', value);
    picker.querySelectorAll('[data-pl-service-tier-option]').forEach(function (item) {
      item.setAttribute('aria-selected', String(item === option));
    });
    if (label) label.textContent = option.textContent.trim();

    this.closeServiceTierPickers();
    if (toggle) toggle.focus();
  };

  PureAssurance.prototype.showTooltip = function (target) {
    if (!target || !this.tooltip) return;

    if (this.activeTooltipTarget && this.activeTooltipTarget !== target) {
      this.activeTooltipTarget.removeAttribute('aria-describedby');
    }

    this.activeTooltipTarget = target;
    this.tooltip.textContent = target.getAttribute('data-tooltip');
    this.tooltip.hidden = false;
    target.setAttribute('aria-describedby', this.tooltip.id);
    this.positionActiveTooltip();
  };

  PureAssurance.prototype.positionActiveTooltip = function () {
    var target = this.activeTooltipTarget;
    var tooltip = this.tooltip;
    var rect;
    var availableRight;
    var tooltipWidth;

    if (!target || !tooltip || tooltip.hidden || !document.documentElement.contains(target)) return;

    rect = target.getBoundingClientRect();
    availableRight = Math.floor(window.innerWidth - rect.right - 16);

    if (availableRight >= 180) {
      tooltipWidth = Math.min(340, availableRight);
      tooltip.style.left = Math.round(window.scrollX + rect.right + 10) + 'px';
      tooltip.style.top = Math.round(window.scrollY + rect.top - 8) + 'px';
      tooltip.setAttribute('data-placement', 'right');
    } else {
      tooltipWidth = Math.min(340, window.innerWidth - 32);
      tooltip.style.left = Math.round(window.scrollX + 16) + 'px';
      tooltip.style.top = Math.round(window.scrollY + rect.bottom + 10) + 'px';
      tooltip.setAttribute('data-placement', 'below');
    }

    tooltip.style.width = Math.max(120, tooltipWidth) + 'px';
  };

  PureAssurance.prototype.hideTooltip = function (target) {
    if (!this.tooltip || (target && target !== this.activeTooltipTarget)) return;
    if (this.activeTooltipTarget) this.activeTooltipTarget.removeAttribute('aria-describedby');
    this.activeTooltipTarget = null;
    this.tooltip.hidden = true;
    this.tooltip.textContent = '';
  };

  PureAssurance.prototype.activateServiceTab = function (activeTab, shouldFocus) {
    var tabs = this.root.querySelectorAll('[data-pl-service-tab]');
    var panels = this.root.querySelectorAll('[data-pl-service-panel]');

    this.hideTooltip();
    this.closeServiceTierPickers();

    tabs.forEach(function (tab) {
      var isActive = tab === activeTab;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    panels.forEach(function (panel) {
      panel.hidden = panel.id !== activeTab.getAttribute('aria-controls');
    });

    if (shouldFocus) activeTab.focus();
  };

  PureAssurance.prototype.onOutsidePointerDown = function (event) {
    if (!this.searchShell.contains(event.target)) this.results.hidden = true;
    if (!event.target.closest('[data-pl-service-tier-picker]')) this.closeServiceTierPickers();
    if (!event.target.closest('.pl-pureassurance__info[data-tooltip]')) this.hideTooltip();
  };

  PureAssurance.prototype.destroy = function () {
    document.removeEventListener('pointerdown', this.handleOutsidePointerDown);
    window.removeEventListener('resize', this.handleTooltipResize);
    this.hideTooltip();
    if (this.tooltip) this.tooltip.remove();
    this.root.classList.remove('is-tooltip-enhanced');
    this.root.plPureAssurance = null;
  };

  PureAssurance.prototype.clearResults = function () {
    this.summary.textContent = '';
    this.state.textContent = '';
    this.exactResults.innerHTML = '';
    this.relatedResults.innerHTML = '';
    this.exactHeading.textContent = 'Exact match';
    this.relatedHeading.textContent = 'Related';
    this.moreResultsButton.hidden = true;
    this.relatedMatches = [];
    this.visibleRelatedCount = 0;
    this.exactMatchCount = 0;
    this.currentQuery = '';
    this.exactSection.hidden = true;
    this.relatedSection.hidden = true;
  };

  PureAssurance.prototype.runSearch = function (query) {
    var trimmed = String(query || '').trim();
    this.clearResults();
    this.currentQuery = trimmed;
    this.results.hidden = false;
    this.clearButton.hidden = !trimmed;

    if (!trimmed) {
      this.summary.textContent = this.root.getAttribute('data-empty-query-message');
      this.state.textContent = this.root.getAttribute('data-empty-query-message');
      return;
    }

    var matches = searchGroups(this.groups, trimmed);
    var contextualMatches = matches.exact.length
      ? []
      : groupContextResults(this.devices.filter(function (device) {
          return device.searchable !== false;
        }), trimmed);

    if (contextualMatches.length) matches.related = contextualMatches;
    var total = matches.exact.length + matches.related.length;

    if (!total) {
      this.summary.textContent = '0 results for “' + trimmed + '”.';
      this.state.textContent = this.root.getAttribute('data-no-results-message');
      return;
    }

    this.summary.textContent = total + (total === 1 ? ' result' : ' results') + ' for “' + trimmed + '”.';

    if (matches.exact.length) {
      this.exactMatchCount = matches.exact.length;
      this.exactSection.hidden = false;
      this.exactHeading.textContent = matches.exact.length + (matches.exact.length === 1 ? ' Exact match' : ' Exact matches');
      this.exactResults.innerHTML = matches.exact.map(this.resultCard.bind(this)).join('');
    }

    if (matches.related.length) {
      this.relatedSection.hidden = false;
      this.relatedHeading.textContent = matches.related.length + ' Related';
      this.relatedMatches = matches.related;
      this.showMoreRelatedResults();
    }
  };

  PureAssurance.prototype.showMoreRelatedResults = function () {
    var nextResults = resultPage(this.relatedMatches, this.visibleRelatedCount);
    var remaining;
    var shownTotal;
    var allResultsTotal;

    if (!nextResults.length) {
      this.moreResultsButton.hidden = true;
      return;
    }

    this.relatedResults.insertAdjacentHTML('beforeend', nextResults.map(this.resultCard.bind(this)).join(''));
    this.visibleRelatedCount += nextResults.length;
    remaining = this.relatedMatches.length - this.visibleRelatedCount;
    this.moreResultsButton.hidden = remaining === 0;
    if (remaining > 0) {
      this.moreResultsButton.setAttribute('aria-label', 'Show ' + Math.min(RESULTS_PAGE_SIZE, remaining) + ' more results');
    }

    shownTotal = this.exactMatchCount + this.visibleRelatedCount;
    allResultsTotal = this.exactMatchCount + this.relatedMatches.length;
    this.summary.textContent = 'Showing ' + shownTotal + ' of ' + allResultsTotal + ' results for “' + this.currentQuery + '”.';
  };

  PureAssurance.prototype.resultCard = function (group) {
    var self = this;
    var modules = group.compatibleModules;
    var packages = group.compatiblePackages;
    var categoryNames = unique(group.categories.map(function (categoryKey) {
      return self.categoryMap[categoryKey] ? self.categoryMap[categoryKey].name : categoryKey;
    }));
    var manufacturerAndCategory = group.manufacturer + (categoryNames.length ? ' · ' + categoryNames.join(', ') : '');
    var badgeRecords = [];
    var seenPrograms = {};
    group.records
      .slice()
      .sort(function (a, b) {
        return self.programMap[a.program].displayOrder - self.programMap[b.program].displayOrder ||
          a.sortOrder - b.sortOrder;
      })
      .forEach(function (record) {
        if (seenPrograms[record.program]) return;
        seenPrograms[record.program] = true;
        badgeRecords.push(record);
      });
    var destinationRecord = badgeRecords[0];
    var badges = badgeRecords
      .map(function (record) {
        var program = self.programMap[record.program];
        return '<span class="pl-pureassurance__badge">' + escapeHtml(program.name) + '</span>';
      }).join('');
    var compatibility = '';
    var primaryLabel = group.isContextResult ? manufacturerAndCategory : group.displayLabel;
    var secondaryLabel = !group.isContextResult && group.secondaryLabel
      ? '<span class="pl-pureassurance__result-model-number">' + escapeHtml(group.secondaryLabel) + '</span>'
      : '';
    var manufacturerLabel = group.isContextResult
      ? ''
      : '<span class="pl-pureassurance__result-manufacturer">' + escapeHtml(manufacturerAndCategory) + '</span>';
    var accessibleIdentity = group.isContextResult
      ? manufacturerAndCategory
      : group.manufacturer + ' ' + group.displayLabel;

    if (modules.length) compatibility += '<p class="pl-pureassurance__compatibility"><strong>Compatible modules:</strong> ' + escapeHtml(modules.join(', ')) + '</p>';
    if (packages.length) compatibility += '<p class="pl-pureassurance__compatibility"><strong>Compatible packages:</strong> ' + escapeHtml(packages.join(', ')) + '</p>';

    return '<article class="pl-pureassurance__result-card" role="button" tabindex="0" data-pl-record-id="' +
      escapeHtml(destinationRecord.id) +
      '" data-pl-program="' +
      escapeHtml(destinationRecord.program) +
      '" aria-label="Show ' +
      escapeHtml(accessibleIdentity) +
      ' in the certified devices directory">' +
      '<div class="pl-pureassurance__result-identity">' +
        '<h3 class="pl-pureassurance__result-model">' + escapeHtml(primaryLabel) + '</h3>' +
        secondaryLabel +
        manufacturerLabel +
      '</div>' +
      '<div class="pl-pureassurance__certifications" aria-label="Certification programs">' +
        '<span class="pl-pureassurance__certified-label"><svg class="pl-pureassurance__icon pl-pureassurance__certified-check" aria-hidden="true" focusable="false"><use href="#pl-icon-check-circle-' + escapeHtml(this.iconScope) + '"></use></svg>Certified for</span>' +
        '<div class="pl-pureassurance__badges">' + badges + '</div>' +
      '</div>' +
      compatibility +
      '</article>';
  };

  PureAssurance.prototype.renderDirectory = function () {
    var self = this;
    this.directory.innerHTML = this.programs.map(function (program) {
      var programDevices = self.devices.filter(function (device) { return device.program === program.key; });
      if (!programDevices.length) return '';
      var buttonId = self.root.id + '-' + program.key + '-button';
      var panelId = self.root.id + '-' + program.key + '-panel';
      var categoryOrder = program.categoryOrder || [];
      var orderedCategories = self.categories.slice().sort(function (a, b) {
        var aIndex = categoryOrder.indexOf(a.key);
        var bIndex = categoryOrder.indexOf(b.key);
        if (aIndex === -1) aIndex = categoryOrder.length + a.displayOrder;
        if (bIndex === -1) bIndex = categoryOrder.length + b.displayOrder;
        return aIndex - bIndex;
      });
      var categories = orderedCategories.map(function (category) {
        var categoryDevices = programDevices
          .filter(function (device) { return device.category === category.key; })
          .sort(function (a, b) {
            return naturalCompare(a.manufacturer, b.manufacturer) ||
              naturalCompare(recordSortValue(a), recordSortValue(b)) ||
              a.sortOrder - b.sortOrder;
          });
        if (!categoryDevices.length) return '';
        return self.categoryTable(program, category, categoryDevices);
      }).join('');

      return '<section class="pl-pureassurance__accordion" data-pl-program-accordion="' + escapeHtml(program.key) + '">' +
        '<h3>' +
          '<button class="pl-pureassurance__accordion-button" id="' + escapeHtml(buttonId) + '" type="button" data-pl-accordion-button aria-expanded="' + String(program.initialExpanded) + '" aria-controls="' + escapeHtml(panelId) + '">' +
            '<span>Certified devices for ' + escapeHtml(program.name) + '</span><span class="pl-pureassurance__chevron" aria-hidden="true"></span>' +
          '</button>' +
        '</h3>' +
        '<div class="pl-pureassurance__accordion-panel" id="' + escapeHtml(panelId) + '" role="region" aria-labelledby="' + escapeHtml(buttonId) + '"' + (program.initialExpanded ? '' : ' hidden') + '>' + categories + '</div>' +
      '</section>';
    }).join('');
  };

  PureAssurance.prototype.categoryTable = function (program, category, devices) {
    var activeColumns = program.columns.filter(function (column) {
      if (!column.conditional) return true;
      return devices.some(function (device) {
        return column.fields.some(function (field) { return hasFieldValue(device, field); });
      });
    });
    var headers = activeColumns.map(function (column) {
      return '<th class="pl-pureassurance__table-heading pl-pureassurance__table-heading--' +
        escapeHtml(column.key) +
        '" scope="col">' +
        escapeHtml(column.label) +
        '</th>';
    }).join('');
    var rows = groupDirectoryDevices(devices).map(function (manufacturerGroup) {
      var records = manufacturerGroup.records;
      var cells = activeColumns.map(function (column) {
        var content;
        var values;

        if (column.key === 'manufacturer') {
          content = escapeHtml(manufacturerGroup.manufacturer);
        } else if (column.key === 'model') {
          values = unique(records.map(function (record) {
            return directoryIdentity(record, 'model');
          }));
          content = escapeHtml(values.join(', '));
        } else if (column.key === 'series') {
          values = unique(records.map(function (record) {
            return directoryIdentity(record, 'series');
          }));
          content = escapeHtml(values.join(', '));
        } else {
          values = unique([].concat.apply([], records.map(function (record) {
            return [].concat.apply([], column.fields.map(function (field) {
              var value = record[field];
              if (Array.isArray(value)) return value;
              return value ? [value] : [];
            }));
          })));
          content = escapeHtml(values.length ? values.join(', ') : '—');
        }

        return '<td class="pl-pureassurance__table-cell pl-pureassurance__table-cell--' +
          escapeHtml(column.key) +
          '" data-label="' +
          escapeHtml(column.label) +
          '">' +
          content +
          '</td>';
      }).join('');
      var recordIds = records.map(function (record) { return record.id; });

      return '<tr class="pl-pureassurance__device-row' +
        '" id="pl-record-' + escapeHtml(recordIds[0]) +
        '" data-pl-record-ids="' + escapeHtml(recordIds.join(' ')) +
        '" tabindex="-1">' + cells + '</tr>';
    }).join('');

    return '<section class="pl-pureassurance__category">' +
      '<h4 class="pl-pureassurance__category-heading">' + escapeHtml(category.name) + '</h4>' +
      '<table class="pl-pureassurance__table pl-pureassurance__table--' +
        escapeHtml(program.key) +
        ' pl-pureassurance__table--columns-' +
        activeColumns.length +
        '">' +
        '<thead><tr>' + headers + '</tr></thead><tbody>' + rows + '</tbody>' +
      '</table>' +
    '</section>';
  };

  PureAssurance.prototype.toggleAccordion = function (button, forceOpen) {
    var expanded = forceOpen === true ? true : button.getAttribute('aria-expanded') !== 'true';
    var panel = this.root.querySelector('#' + button.getAttribute('aria-controls'));
    button.setAttribute('aria-expanded', String(expanded));
    panel.hidden = !expanded;
  };

  PureAssurance.prototype.revealRecord = function (recordId, programKey) {
    var accordion = this.directory.querySelector('[data-pl-program-accordion="' + programKey + '"]');
    var button = accordion && accordion.querySelector('[data-pl-accordion-button]');
    var row = this.directory.querySelector('[data-pl-record-ids~="' + recordId + '"]');
    if (!button || !row) return;

    this.toggleAccordion(button, true);
    row.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
    row.focus({ preventScroll: true });
    row.classList.add('is-highlighted');
    window.setTimeout(function () { row.classList.remove('is-highlighted'); }, HIGHLIGHT_DURATION);
  };

  function init() {
    document.querySelectorAll('[data-pl-pureassurance]').forEach(function (root) {
      if (!root.plPureAssurance) root.plPureAssurance = new PureAssurance(root);
    });
  }

  window.PLPureAssurance = {
    normalizeText: normalizeText,
    compact: compact,
    listValue: listValue,
    normalizeDevices: normalizeDevices,
    ensureConfigurations: ensureConfigurations,
    tokensMatchFields: tokensMatchFields,
    groupDevices: groupDevices,
    groupDirectoryDevices: groupDirectoryDevices,
    directoryIdentity: directoryIdentity,
    contextMatchesRecord: contextMatchesRecord,
    groupContextResults: groupContextResults,
    resultPage: resultPage,
    exactMatch: exactMatch,
    relatedScore: relatedScore,
    searchGroups: searchGroups
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-pl-pureassurance]');
    if (root && !root.plPureAssurance) root.plPureAssurance = new PureAssurance(root);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.matches('[data-pl-pureassurance]')
      ? event.target
      : event.target.querySelector('[data-pl-pureassurance]');
    if (root && root.plPureAssurance) root.plPureAssurance.destroy();
  });
}());
